/**
 * <pdp-stepped-config> — wizard/accordion controller for the stepped
 * product configurator layout.
 *
 * Behaviour:
 *  - Click on a step header toggles it (collapsing others).
 *  - When the currently-open step emits a change in its control (variant
 *    selected, frame color clicked, order-type toggled, etc.), auto-
 *    advance to the next step and mark the previous one as completed.
 *  - Keyboard navigation via Tab / Enter / Space handled by the native
 *    <button> elements inside step headers.
 *
 * The Liquid pre-renders each step with `data-step="N"`; this element
 * just wires interactivity on top.
 */

class PdpSteppedConfig extends HTMLElement {
  #abortController;
  #observer;
  #renumberPending = false;
  // Until the customer touches a step, the wizard keeps the FIRST visible
  // step open. Async data (frame colours) can reveal an earlier step after
  // load, and it should take over as the open one. Once they interact we
  // stop auto-moving and only intervene if the open step gets hidden.
  #userInteracted = false;
  #pristineFallbackArmed = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#renumber();
    // Liquid pre-opens the first step. If Alpine ends up hiding it (e.g.
    // frame-color step on a frame with no colors), shift the open state
    // to the first visible step. No scroll on initial load.
    this.#ensureOpenStepIsVisible({ scroll: false });
    // Alpine templates inject/remove step cards dynamically (e.g. one per
    // rx lens option). Watch the subtree and keep step numbers in sync.
    this.#observer = new MutationObserver(() => this.#scheduleRenumber());
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'hidden'],
    });

    this.addEventListener('click', (e) => this.#handleClick(e), { signal });

    // Auto-advance heuristic: any click on an option inside the currently
    // open step's content that isn't the header itself moves to the next.
    // Scoped to the content so expanding a step doesn't count as a choice.
    // `.pdp-tag--custom-size` is excluded: it wraps a native <select>, and a
    // click there just *opens* the dropdown — the real choice arrives later
    // as a `change` event (handled below). Advancing on the open-click would
    // skip the step before the customer picks a size.
    this.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest(
          '.pdp-tag:not(.pdp-tag--custom-size), .pdp-frame-swatch, .pdp-order-toggle__option, .pdp-eyewears-tabs__tab, .pdp-coating-option'
        );
        if (!btn) return;
        this.#markChoiceAndAdvance(btn);
      },
      { signal }
    );

    // Custom-size dropdown: only a real selection (change) counts as a
    // choice, not opening the picker.
    this.addEventListener(
      'change',
      (e) => {
        const select = e.target.closest('select');
        if (!select) return;
        this.#markChoiceAndAdvance(select);
      },
      { signal }
    );
  }

  /**
   * A control inside a step registered a real choice — drop the step's
   * pristine flag (so active visuals appear) and advance to the next step.
   * No-op if the control isn't inside a step's content.
   */
  #markChoiceAndAdvance(control) {
    const content = control.closest('.pdp-step__content');
    if (!content) return;
    const step = content.closest('.pdp-step');
    if (!step) return;
    this.#userInteracted = true;
    step.removeAttribute('data-pristine');
    requestAnimationFrame(() => this.#advanceAfter(step));
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  #scheduleRenumber() {
    if (this.#renumberPending) return;
    this.#renumberPending = true;
    requestAnimationFrame(() => {
      this.#renumberPending = false;
      this.#renumber();
      // Visibility may have just changed (orderType toggle, frame swap…).
      // If the currently-open step was hidden by that change, jump the
      // open state to the next visible step so the customer always has
      // something to fill in.
      this.#ensureOpenStepIsVisible();
    });
  }

  #renumber() {
    // Count only steps that are not currently hidden via Alpine's x-show
    // (display:none) so the visible sequence reads 1..N without gaps.
    const allSteps = Array.from(this.querySelectorAll('.pdp-step'));
    let n = 0;
    allSteps.forEach((step) => {
      const numSpan = step.querySelector('.pdp-step__number');
      if (this.#isHidden(step)) {
        if (numSpan) numSpan.textContent = '';
        return;
      }
      n += 1;
      if (numSpan) numSpan.textContent = n;
    });
  }

  #isHidden(step) {
    return step.style.display === 'none' || step.hasAttribute('hidden');
  }

  #getVisibleSteps() {
    return Array.from(this.querySelectorAll('.pdp-step')).filter((s) => !this.#isHidden(s));
  }

  /**
   * If the currently-open step is hidden (or there is no open step at all
   * during init), pick the first visible step at-or-after its position
   * and open that one instead. Does nothing if everything is already in
   * order, or if every step is hidden.
   */
  #ensureOpenStepIsVisible({ scroll = true } = {}) {
    const all = Array.from(this.querySelectorAll('.pdp-step'));
    const visible = all.filter((s) => !this.#isHidden(s));
    if (visible.length === 0) return;

    const open = this.querySelector('.pdp-step.pdp-step--open');
    const openVisible = open && !this.#isHidden(open);

    if (!this.#userInteracted) {
      // If a step BEFORE the first visible one is hidden, it's most likely
      // waiting on async data (frame colours load via fetch). Don't flash a
      // later step (frame size) open over it — keep the Liquid pre-opened
      // first step; it'll claim the slot once it appears, so the customer
      // only ever sees it open, never a flicker. #armPristineFallback covers
      // the case where it never appears (product genuinely has no colours).
      const earlierHidden = all.indexOf(visible[0]) > 0;
      if (earlierHidden && !openVisible) {
        this.#armPristineFallback();
        return;
      }
      // First visible step is the earliest step (or fallback already ran) —
      // make sure it's the open one.
      if (open === visible[0]) return;
      this.#closeAll();
      this.#openStep(visible[0], { scroll: false });
      return;
    }

    // Post-interaction: only intervene if the open step got hidden.
    if (openVisible) return;
    let target = visible[0];
    if (open) {
      const idx = all.indexOf(open);
      target = visible.find((s) => all.indexOf(s) > idx) || visible[0];
    }
    this.#closeAll();
    this.#openStep(target, { scroll });
  }

  /**
   * One-shot safety net for the pre-interaction phase: if the intended first
   * step stays hidden (e.g. a product with no alternate colours, so the frame
   * colour step never shows), open the first visible step after a short grace
   * period. No-op if an earlier step appeared and is already open, or if the
   * customer has since interacted.
   */
  #armPristineFallback() {
    if (this.#pristineFallbackArmed) return;
    this.#pristineFallbackArmed = true;
    setTimeout(() => {
      if (this.#userInteracted) return;
      const open = this.querySelector('.pdp-step.pdp-step--open');
      if (open && !this.#isHidden(open)) return;
      const visible = this.#getVisibleSteps();
      if (visible.length === 0) return;
      this.#closeAll();
      this.#openStep(visible[0], { scroll: false });
    }, 1200);
  }

  #handleClick(e) {
    const toggle = e.target.closest('[data-step-toggle]');
    if (!toggle) return;
    e.preventDefault();
    const step = toggle.closest('.pdp-step');
    if (!step) return;
    // Manually opening a step is an interaction — stop auto-syncing the open
    // step to the first visible one.
    this.#userInteracted = true;
    const isOpen = step.classList.contains('pdp-step--open');
    // Accordion: close everything, then (re)open the clicked step only if
    // it was previously closed.
    this.#closeAll();
    if (!isOpen) this.#openStep(step);
  }

  #closeAll() {
    this.querySelectorAll('.pdp-step.pdp-step--open').forEach((s) => {
      s.classList.remove('pdp-step--open');
      const header = s.querySelector('.pdp-step__header');
      header?.setAttribute('aria-expanded', 'false');
    });
  }

  #openStep(step, { scroll = true } = {}) {
    step.classList.add('pdp-step--open');
    const header = step.querySelector('.pdp-step__header');
    header?.setAttribute('aria-expanded', 'true');
    // Smooth-scroll the newly-opened step into view on small screens so
    // the customer doesn't have to hunt for the freshly-revealed content.
    // Suppressed on initial load to avoid auto-scrolling the page.
    if (scroll && window.matchMedia('(max-width: 1023px)').matches) {
      this.#scrollStepIntoView(step);
    }
  }

  /**
   * Scroll a step into view only AFTER the open/close collapse animations
   * have settled. Scrolling immediately is wrong: while the previous step is
   * still collapsing (grid-template-rows transition), the content above the
   * target shrinks, so the browser scrolls to a stale position and the step
   * ends up too high. We wait for the `grid-template-rows` transitionend
   * (with a timeout fallback in case nothing was actually animating), then
   * scroll against the final, stable layout. `scroll-margin-top` on
   * `.pdp-step` keeps it clear of the sticky header.
   */
  #scrollStepIntoView(step) {
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      this.removeEventListener('transitionend', onEnd);
      step.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const onEnd = (e) => {
      if (e.propertyName === 'grid-template-rows') run();
    };
    this.addEventListener('transitionend', onEnd);
    const timer = setTimeout(run, 350);
  }

  #advanceAfter(step) {
    step.classList.add('pdp-step--completed');
    this.#updateSelectedLabel(step);
    const steps = Array.from(this.querySelectorAll('.pdp-step'));
    const i = steps.indexOf(step);
    // Skip over hidden steps so the customer doesn't land on something
    // they can't interact with.
    let next = null;
    for (let j = i + 1; j < steps.length; j += 1) {
      if (!this.#isHidden(steps[j])) {
        next = steps[j];
        break;
      }
    }
    if (!next) return;
    this.#closeAll();
    this.#openStep(next);
  }

  /**
   * Scrape the currently-selected option out of the step's content and
   * write a short summary into its `.pdp-step__selected` span so the
   * customer sees what they picked once the step collapses.
   */
  #updateSelectedLabel(step) {
    const slot = step.querySelector('.pdp-step__selected');
    if (!slot) return;
    const content = step.querySelector('.pdp-step__content');
    if (!content) return;

    let text = '';

    const activeTag = content.querySelector('.pdp-tag--active');
    if (activeTag) {
      if (activeTag.tagName === 'SELECT') {
        // Custom-size dropdown: the active class sits on the <select> itself,
        // so read the chosen option rather than its concatenated option list.
        const opt = activeTag.selectedOptions && activeTag.selectedOptions[0];
        text = (opt && opt.value ? opt.textContent : '').trim().replace(/\s+/g, ' ');
      } else {
        const val = activeTag.querySelector('.pdp-tag__value');
        text = (val ? val.textContent : activeTag.textContent).trim().replace(/\s+/g, ' ');
      }
    }

    if (!text) {
      const activeSwatch = content.querySelector('.pdp-frame-swatch--active');
      if (activeSwatch) {
        // Preferred: explicit data-selected-label (set on lens colour
        // swatches where no data-color-swatch chip exists).
        text = activeSwatch.getAttribute('data-selected-label') || '';
        if (!text) {
          text = (activeSwatch.getAttribute('aria-label') || '')
            .replace(/^Change [^:]+to /i, '').trim();
        }
        if (!text) {
          const chip = activeSwatch.querySelector('[data-color-swatch]');
          text = chip?.getAttribute('data-color-swatch') || '';
        }
        // Little flourish: on swatch-driven steps (frame colour, lens
        // colour) paint the number circle with the chosen colour instead
        // of a digit / checkmark.
        const kind = step.dataset.stepKind;
        if (kind === 'swatches' || kind === 'frame_color_switcher') {
          this.#paintNumberWithSwatch(step, activeSwatch);
        }
      }
    }

    if (!text) {
      const activeToggle = content.querySelector('.pdp-order-toggle__option--active');
      if (activeToggle) text = activeToggle.textContent.trim();
    }

    if (!text) {
      const activeTab = content.querySelector('.pdp-eyewears-tabs__tab--active');
      if (activeTab) {
        const label = activeTab.querySelector('.pdp-eyewears-tabs__label');
        text = (label ? label.textContent : activeTab.textContent).trim();
      }
    }

    if (!text) {
      const activeCoating = content.querySelector('.pdp-coating-option--active');
      if (activeCoating) {
        const label = activeCoating.querySelector('.pdp-coating-option__label');
        text = label ? `Added · ${label.textContent.trim()}` : 'Added';
      } else if (content.querySelector('.pdp-coating-option')) {
        // Step was touched but coating is OFF → user explicitly declined
        text = 'Skipped';
      }
    }

    slot.textContent = text;
  }

  /**
   * Paint the step's number circle with the swatch colour so it reads as a
   * tiny preview of the customer's pick. Works for both `background-image`
   * (lens colour) and `background-color` (frame tint) chips.
   */
  #paintNumberWithSwatch(step, activeSwatch) {
    const numSpan = step.querySelector('.pdp-step__number');
    const chip = activeSwatch.querySelector('.pdp-frame-swatch__chip, [data-color-swatch]');
    if (!numSpan || !chip) return;
    const styles = getComputedStyle(chip);
    numSpan.style.backgroundImage = styles.backgroundImage;
    numSpan.style.backgroundColor = styles.backgroundColor;
    numSpan.style.backgroundSize = 'cover';
    numSpan.style.backgroundPosition = 'center';
    step.classList.add('pdp-step--has-color-preview');
  }
}

if (!customElements.get('pdp-stepped-config')) {
  customElements.define('pdp-stepped-config', PdpSteppedConfig);
}
